import {
  CAMERA_SELECTOR_CONFIRM_EVENT,
  CAMERA_SELECTOR_NAVIGATE_EVENT,
  TOGGLE_FOCUSED_CAMERA_SELECTOR_EVENT,
} from '@/providers/gamepadProvider/gamepadProvider.constants.ts';
import { compareGridReadingOrder } from '@/utils/gridReadingOrder.ts';
import { CAMERA_STREAM_WIDGET } from '@/widgetComponentNames';

const FOCUSABLE_SELECTOR = `
 a[href],
  area[href],
  button,
  [tabindex],
  [contenteditable="true"]
`.replace(/\s+/g, ' ');

const isVisible = (el: Element) => {
  const cssStyleDeclaration = getComputedStyle(el as HTMLElement);
  return cssStyleDeclaration.visibility !== 'hidden' && cssStyleDeclaration.display !== 'none';
};

const isFocusable = (el: Element) => {
  const e = el as HTMLElement;

  // skip disabled form controls
  if (e.matches('button[disabled]')) {
    return false;
  }
  const tabIndexAttr = el.getAttribute('tabindex');
  const tabIndex = tabIndexAttr !== null ? Number(tabIndexAttr) : NaN;

  // Explicit tabindex:
  //  - < 0  → NOT focusable (e.g. tabindex="-1")
  //  - >= 0 → focusable
  if (!Number.isNaN(tabIndex)) {
    return tabIndex >= 0;
  }
  // native focusables or anything with tabindex
  return e.matches(FOCUSABLE_SELECTOR);
};

/**
 * Returns all visible and focusable elements matching the given selector.
 *
 * If no selector is provided, the global `FOCUSABLE_SELECTOR` is used.
 * The result is filtered through `isFocusable()` and `isVisible()`.
 *
 * @param selector - Optional CSS selector to narrow which elements are checked.
 * @returns An array of visible, focusable HTMLElements.
 *
 * @example
 * getFocusableInDocument(); // all focusables
 * getFocusableInDocument('[data-component="CameraStreamWidget"]'); // Video widget roots only
 */
const getFocusableInDocument = (selector: string = FOCUSABLE_SELECTOR): HTMLElement[] => {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(selector));
  return nodes.filter(isFocusable).filter(isVisible);
};

const CAMERA_WIDGET_FOCUSABLE_SELECTOR = `[data-component="${CAMERA_STREAM_WIDGET}"]`;
const TOGGLE_CAMERA_SELECTOR_DEBOUNCE_MS = 220;
let lastToggleCameraSelectorAt = 0;
export type CameraSelectorNavigateDirection = 'up' | 'down' | 'left' | 'right';

/** Set on `CameraStreamWidgetWrapper` when `state === 'connected'` — mirrors live stream / camera availability. */
const isCameraStreamWidgetConnected = (root: HTMLElement): boolean =>
  root.dataset.cameraStreamConnected === 'true';

const GRID_STACK_ITEM_SELECTOR = '.grid-stack-item';

/**
 * Reading order on the landing grid: smaller `gs-y` first, then smaller `gs-x`. Roots not under a
 * grid item (e.g. Camera Check page) keep document-relative order at the end.
 */
const sortCameraStreamRootsByGridReadingOrder = (roots: HTMLElement[]): HTMLElement[] => {
  const scored = roots.map((el, domIndex) => {
    const item = el.closest(GRID_STACK_ITEM_SELECTOR);
    if (!item) {
      return {
        el,
        y: Number.POSITIVE_INFINITY,
        x: Number.POSITIVE_INFINITY,
        id: '',
        tie: domIndex,
      };
    }
    const yRaw = Number(item.getAttribute('gs-y'));
    const xRaw = Number(item.getAttribute('gs-x'));
    const y = Number.isFinite(yRaw) ? yRaw : Number.POSITIVE_INFINITY;
    const x = Number.isFinite(xRaw) ? xRaw : Number.POSITIVE_INFINITY;
    const id = item.getAttribute('gs-id') ?? '';
    return { el, y, x, id, tie: domIndex };
  });

  scored.sort((a, b) => compareGridReadingOrder(a, b));

  return scored.map(s => s.el);
};

function getFullscreenElement(): Element | null {
  return (
    document.fullscreenElement ??
    (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ??
    null
  );
}

/**
 * The camera stream card that is currently fullscreen — i.e. the widget root that owns
 * `requestFullscreen`, not whatever happens to have focus (often `body` when using gamepad only).
 */
const resolveFullscreenCameraStreamRoot = (): HTMLElement | null => {
  const fs = getFullscreenElement();
  if (!(fs instanceof HTMLElement)) return null;
  if (fs.matches(CAMERA_WIDGET_FOCUSABLE_SELECTOR)) return fs;
  return fs.closest<HTMLElement>(CAMERA_WIDGET_FOCUSABLE_SELECTOR);
};

async function exitFullscreenAndSettle(): Promise<void> {
  if (!getFullscreenElement()) return;
  const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> };
  try {
    await (document.exitFullscreen?.() ?? doc.webkitExitFullscreen?.() ?? Promise.resolve());
  } catch {
    /* user cancel or not allowed */
  }
  await new Promise<void>(resolve => {
    if (!getFullscreenElement()) {
      resolve();
      return;
    }
    const finish = () => {
      if (!getFullscreenElement()) {
        cleanup();
        resolve();
      }
    };
    const cleanup = () => {
      document.removeEventListener('fullscreenchange', finish);
      document.removeEventListener('webkitfullscreenchange', finish);
    };
    document.addEventListener('fullscreenchange', finish);
    document.addEventListener('webkitfullscreenchange', finish);
    window.setTimeout(() => {
      cleanup();
      resolve();
    }, 450);
  });
}

function requestFullscreenOnElement(el: HTMLElement): Promise<void> {
  const anyEl = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
  const p = anyEl.requestFullscreen?.() ?? anyEl.webkitRequestFullscreen?.();
  return Promise.resolve(p as Promise<void>);
}

/**
 * Same behavior as Alt+Enter / F on the focused camera stream widget: enter fullscreen only when the
 * stream is connected (`data-camera-stream-connected`). When already fullscreen on a camera card,
 * always exit — including after disconnect — so the toggle never traps the user; focus may have moved.
 */
export const toggleFocusedCameraStreamFullscreen = () => {
  const fs = getFullscreenElement();
  const active = document.activeElement as HTMLElement | null;

  const fullscreenCamera =
    fs instanceof HTMLElement && fs.matches(CAMERA_WIDGET_FOCUSABLE_SELECTOR) ? fs : null;
  const focusedCamera = active?.closest<HTMLElement>(CAMERA_WIDGET_FOCUSABLE_SELECTOR) ?? null;

  if (fullscreenCamera) {
    try {
      void document.exitFullscreen?.();
    } catch {
      /* denied or unsupported */
    }
    return;
  }

  if (!focusedCamera || focusedCamera.dataset.cameraStreamConnected !== 'true') return;

  try {
    void requestFullscreenOnElement(focusedCamera);
  } catch {
    /* denied or unsupported */
  }
};

/** One in-flight navigate (Alt+arrow or L2/R2); avoids stacked exit/move/enter fullscreen chains. */
let navigateCameraWidgetsBusy = false;

/**
 * Alt+arrow, L2/R2, and RB/LB “camera-only” focus steps only visit widgets with an active stream.
 * Disconnected or missing-camera cards stay out of the sequence so we never land on or fullscreen them.
 */
const getSortedConnectedCameraStreamRoots = (): HTMLElement[] =>
  sortCameraStreamRootsByGridReadingOrder(
    getFocusableInDocument(CAMERA_WIDGET_FOCUSABLE_SELECTOR)
  ).filter(isCameraStreamWidgetConnected);

/**
 * Deterministic next/prev stream card — **no** `moveFocus` debounce. After `exitFullscreen`, the
 * global debounce in `moveFocus` could ignore the navigation call (same dir within 120ms from a
 * prior poll frame), yielding `null` focus and skipping re-fullscreen → looks like a random drop to
 * windowed mode.
 */
const getAdjacentCameraStreamCard = (currentCard: HTMLElement, dir: 1 | -1): HTMLElement | null => {
  const list = getSortedConnectedCameraStreamRoots();
  const idx = list.indexOf(currentCard);
  if (idx < 0) return null;
  let next = idx + dir;
  if (next < 0) next = list.length - 1;
  if (next >= list.length) next = 0;
  return list[next] ?? null;
};

/**
 * Shared by **keyboard (Alt+arrow)** and **gamepad (L2/R2)**.
 *
 * **Fullscreen:** resolve the fullscreen camera root, compute the **target card before** any async
 * work, exit fullscreen, then focus that element and re-enter fullscreen when connected. We do **not**
 * route this through `moveFocus` — its shared debounce can swallow the step right after exit, and
 * `document.activeElement` after exit is unreliable when using gamepad only.
 *
 * **Not fullscreen:** delegate to `moveFocus({ justCameraWidget })` only.
 */
export async function moveFocusCameraWidgetExitFullscreenThenNavigate(dir: 1 | -1): Promise<void> {
  if (navigateCameraWidgetsBusy) return;
  navigateCameraWidgetsBusy = true;
  try {
    const fullscreenCameraCard = resolveFullscreenCameraStreamRoot();

    if (fullscreenCameraCard) {
      const nextCard = getAdjacentCameraStreamCard(fullscreenCameraCard, dir);

      // Single connected stream: wrap lands on the same card — skip exit/focus/re-fullscreen (flicker).
      if (nextCard === fullscreenCameraCard) {
        return;
      }

      await exitFullscreenAndSettle();

      if (!nextCard) {
        moveFocus({ dir, justCameraWidget: true });
        return;
      }

      nextCard.focus({ preventScroll: false });
      if (!nextCard.matches(':focus-visible')) {
        nextCard.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      lastMoveDir = dir;
      lastMoveAt = performance.now();

      try {
        await requestFullscreenOnElement(nextCard);
      } catch {
        /* denied or unsupported */
      }
      return;
    }

    moveFocus({ dir, justCameraWidget: true });
  } finally {
    navigateCameraWidgetsBusy = false;
  }
}

/**
 * **Gamepad-only entry:** L2/R2 are handled in `GamepadProvider`’s global RAF loop — not inside the
 * camera widget like keyboard hotkeys (`scopeRef` + focus). Calling the same `moveFocusCamera…`
 * helper keeps behavior identical to Alt+arrow; do not replace this with raw `moveFocus` here,
 * or fullscreen transitions will lose the anchor and jump to arbitrary streams.
 */
export const gamepadNavigateBetweenCameraStreams = (dir: 1 | -1): void => {
  void moveFocusCameraWidgetExitFullscreenThenNavigate(dir);
};

/**
 * Stamps `lastToggleCameraSelectorAt` to now so the debounce window restarts.
 * Call this whenever a camera-selector modal is closed via a path that does NOT
 * go through `toggleFocusedCameraSelector` (e.g. a direct close action in the
 * modal's own input map) to prevent the still-held button from immediately
 * re-triggering the toggle on the next RAF poll.
 */
export const stampCameraSelectorToggleTime = () => {
  lastToggleCameraSelectorAt = performance.now();
};

/**
 * Broadcasts a request to toggle the camera selector modal for the currently focused camera widget.
 * Widgets decide locally whether to open (focused) or close (already open).
 *
 * When the document is in **fullscreen** (e.g. gamepad toggled video fullscreen), the modal would
 * otherwise render behind the fullscreen layer and steal focus from the stream. In that case we
 * exit fullscreen first, restore focus on the camera widget root, then dispatch so the modal opens
 * in normal stacking order while gamepad/keyboard input stays usable.
 */
export const toggleFocusedCameraSelector = (): void => {
  void toggleFocusedCameraSelectorAsync();
};

const toggleFocusedCameraSelectorAsync = async (): Promise<void> => {
  const now = performance.now();
  if (now - lastToggleCameraSelectorAt < TOGGLE_CAMERA_SELECTOR_DEBOUNCE_MS) {
    return;
  }
  lastToggleCameraSelectorAt = now;

  const fsBeforeExit = getFullscreenElement();
  if (!fsBeforeExit) {
    window.dispatchEvent(new Event(TOGGLE_FOCUSED_CAMERA_SELECTOR_EVENT));
    return;
  }

  const focusTarget: HTMLElement | null =
    resolveFullscreenCameraStreamRoot() ??
    (fsBeforeExit instanceof HTMLElement ? fsBeforeExit : null);

  await exitFullscreenAndSettle();

  if (focusTarget && document.contains(focusTarget)) {
    focusTarget.focus({ preventScroll: false });
    if (!focusTarget.matches(':focus-visible')) {
      focusTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  window.dispatchEvent(new Event(TOGGLE_FOCUSED_CAMERA_SELECTOR_EVENT));
};

export const navigateCameraSelector = (direction: CameraSelectorNavigateDirection) => {
  window.dispatchEvent(
    new CustomEvent<CameraSelectorNavigateDirection>(CAMERA_SELECTOR_NAVIGATE_EVENT, {
      detail: direction,
    })
  );
};

export const confirmCameraSelector = () => {
  window.dispatchEvent(new Event(CAMERA_SELECTOR_CONFIRM_EVENT));
};

let lastMoveDir: 1 | -1 | null = null;
let lastMoveAt = 0;
const MOVE_DEBOUNCE_MS = 120; // debounce time in milliseconds

/**
 * Moves focus forward/backward through all focusable elements in the document.
 *
 * Includes fast-press protection: if the gamepad triggers the same direction
 * twice within a short window (MOVE_DEBOUNCE_MS), only the first move is applied.
 * This ensures one physical button press results in exactly one focus step,
 * even if the gamepad API reports multiple rapid "pressed" states.
 *
 * @param dir 1 to move forward, -1 to move backward
 */
type moveFocusParams = {
  dir: 1 | -1;

  justCameraWidget?: boolean;
};
const moveFocus = ({ dir, justCameraWidget = false }: moveFocusParams): HTMLElement | null => {
  const now = performance.now();

  // 🔒 Debounce: if same direction is requested again too quickly, ignore it
  if (lastMoveDir === dir && now - lastMoveAt < MOVE_DEBOUNCE_MS) {
    return null;
  }
  lastMoveDir = dir;
  lastMoveAt = now;

  const list = justCameraWidget ? getSortedConnectedCameraStreamRoots() : getFocusableInDocument();

  if (!list.length) return null;

  const active = (document.activeElement as HTMLElement) ?? null;

  let idx: number;
  if (justCameraWidget) {
    // Disconnected widgets are not in `list`; index stays -1 so focus moves to a connected card.
    const fsEl = getFullscreenElement();
    const currentEl =
      (active && list.includes(active)
        ? active
        : active?.closest<HTMLElement>(CAMERA_WIDGET_FOCUSABLE_SELECTOR)) ??
      (fsEl instanceof HTMLElement && fsEl.matches(CAMERA_WIDGET_FOCUSABLE_SELECTOR) ? fsEl : null);
    idx = currentEl ? list.indexOf(currentEl) : -1;
  } else {
    idx = active ? list.indexOf(active) : -1;
  }

  if (idx === -1) {
    // nothing focused yet → start before/after list depending on direction
    idx = dir > 0 ? -1 : 0;
  }

  let next = idx + dir;
  if (next < 0) next = list.length - 1;
  if (next >= list.length) next = 0;

  const target = list[next];
  if (target) {
    target.focus({ preventScroll: false });
    if (!target.matches(':focus-visible')) {
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    return target;
  }
  return null;
};

/** Focuses the first **connected** camera stream widget, if any (e.g. when opening Camera Check). */
export const focusFirstCameraStreamWidget = () => {
  const list = getSortedConnectedCameraStreamRoots();
  const target = list[0];
  if (!target) return;
  target.focus({ preventScroll: false });
  if (!target.matches(':focus-visible')) {
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
};

export const activate = () => {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return;

  if (typeof el.click === 'function') {
    el.click();
    return;
  }

  el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true }));
  el.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true }));
};
